import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { SectionHeading } from "../components/PageBits";
import { Reveal, RevealLines } from "../components/Reveal";

export default function Events() {
  const notify = () => toast("Aún no hay eventos publicados. Muy pronto compartiremos las próximas convocatorias.");

  return (
    <>
      {/* 1. Hero */}
      <section className="grain relative overflow-hidden bg-navy pt-40 pb-16 sm:pt-48 sm:pb-24" data-testid="events-hero">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[150px]" />
        </div>
        <div className="container-luxe relative z-10">
          <Reveal>
            <p className="overline mb-7 flex items-center gap-4">
              <span className="h-px w-10 bg-gold" /> Eventos
            </p>
          </Reveal>
          <h1 className="max-w-4xl font-serif text-4xl font-light leading-[1.06] tracking-tight text-cream sm:text-5xl lg:text-6xl">
            <RevealLines lines={["Algunas transformaciones", "necesitan detener el ritmo"]} delay={0.2} />
            <RevealLines lines={["para llegar más profundo."]} lineClassName="italic text-gold" delay={0.44} />
          </h1>
          <div className="mt-8 max-w-2xl space-y-5">
            <Reveal delay={0.6}>
              <p className="text-base font-light leading-relaxed text-cream/75 sm:text-lg">
                Hay procesos que requieren algo diferente: más tiempo, más presencia y un espacio
                dedicado exclusivamente a mirar hacia adentro.
              </p>
            </Reveal>
            <Reveal delay={0.7}>
              <p className="text-base font-light leading-relaxed text-cream/75 sm:text-lg">
                Los Eventos de la Academia son encuentros diseñados para vivir esa experiencia con
                calma, profundidad y acompañamiento.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. ¿Por qué existen estos encuentros? */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="events-why">
        <div className="container-luxe">
          <SectionHeading
            dark={false}
            overline="Una experiencia diferente"
            title="No todo puede vivirse en"
            accent="una sesión semanal."
          />
          <div className="max-w-3xl space-y-6">
            <Reveal>
              <p className="text-lg font-light leading-relaxed text-navy/80">
                Algunos temas necesitan un espacio donde puedas detenerte, profundizar y sostener el
                proceso sin las interrupciones del día a día.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-lg font-light leading-relaxed text-navy/80">
                Los Eventos crean ese contexto para trabajar con mayor presencia, reflexión y conexión.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. ¿Cómo se viven? */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="events-experience">
        <div className="container-luxe relative z-10">
          <SectionHeading overline="La experiencia" title="Tiempo para" accent="profundizar." />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <p className="text-lg font-light leading-relaxed text-cream/75">
                Cada encuentro está diseñado para combinar aprendizaje, reflexión, diálogo y práctica
                en un ambiente donde el tiempo deja de ser una limitación.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-cream">
                No se trata de recibir más información.
                <br />
                <span className="italic text-gold">Se trata de vivir una experiencia transformadora.</span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 4. Formato */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="events-format">
        <div className="container-luxe">
          <SectionHeading dark={false} overline="Próximos encuentros" title="Cada evento" accent="es único." />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <p className="text-lg font-light leading-relaxed text-navy/80">
                La modalidad, duración y formato se anunciarán junto con cada convocatoria.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5">
              <p className="font-serif text-3xl font-light leading-snug text-navy">
                Algunos encuentros podrán ser virtuales. Otros presenciales.
                <br />
                <span className="italic text-gold-deep">
                  Cada uno conservará la misma esencia: ofrecer un espacio para profundizar con
                  intención y presencia.
                </span>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 5. ¿Para quién son? */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="events-audience">
        <div className="container-luxe relative z-10">
          <SectionHeading overline="¿A quién están dirigidos?" title="Para quienes desean" accent="ir un paso más allá." />
          <Reveal>
            <p className="max-w-3xl text-lg font-light leading-relaxed text-cream/75">
              Estos encuentros están dirigidos a quienes desean profundizar su proceso personal dentro
              de la metodología de la Academia y vivir experiencias que complementen el recorrido
              habitual.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 6. Cierre */}
      <section className="grain relative overflow-hidden bg-navy-deep py-24 sm:py-32" data-testid="events-closing">
        <div className="container-luxe relative z-10 max-w-3xl">
          <Reveal>
            <p className="font-serif text-2xl font-light leading-snug text-cream sm:text-3xl">
              Cada evento es una oportunidad para detener el ritmo, mirar con mayor profundidad y seguir
              avanzando con <span className="italic text-gold">una comprensión más amplia de ti mismo</span>.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <button onClick={notify} className="btn-gold mt-10" data-testid="events-cta">
              Ver próximos eventos <ArrowRight size={15} />
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
